import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

export default function AdminPage() {
  const { user, role, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [tab, setTab] = useState(searchParams.get('tab') || 'listings')
  const [listings, setListings] = useState([])
  const [posts, setPosts]       = useState([])
  const [users, setUsers]       = useState([])
  const [reports, setReports]     = useState([])
  const [removals, setRemovals]   = useState([])
  const [claims, setClaims]       = useState([])
  const [stats, setStats]       = useState({})
  const [msg, setMsg]           = useState('')
  const [loading, setLoading]   = useState(true)

  // Auth guard
  useEffect(() => {
    if (!authLoading && (!user || role < 100)) navigate('/')
  }, [user, role, authLoading, navigate])

  useEffect(() => { if (role >= 100) fetchAll() }, [role])

  async function fetchAll() {
    setLoading(true)
    const [{ data: l }, { data: p }, { data: u }, { data: r }, { data: rem }, { data: cl }, { count: lc }, { count: pc }] = await Promise.all([
      supabase.from('listings').select('*').order('created_at', { ascending:false }),
      supabase.from('blog_posts').select('*').order('created_at', { ascending:false }),
      role >= 300 ? supabase.from('profiles').select('*').order('created_at', { ascending:false }) : { data: [] },
      supabase.from('reports').select('*').eq('status','open').order('created_at', { ascending:false }),
      supabase.from('removal_requests').select('*').eq('status','open').order('created_at', { ascending:false }),
      supabase.from('ownership_claims').select('*, listings(name,city,country)').eq('status','pending').order('created_at', { ascending:false }),
      supabase.from('listings').select('*', { count:'exact', head:true }),
      supabase.from('blog_posts').select('*', { count:'exact', head:true }),
    ])
    setListings(l || [])
    setPosts(p || [])
    setUsers(u || [])
    setReports(r || [])
    setRemovals(rem || [])
    setClaims(cl || [])
    setStats({ listings: lc || 0, posts: pc || 0 })
    setLoading(false)
  }

  function toast(m) { setMsg(m); setTimeout(() => setMsg(''), 3000) }

  // ── LISTING ACTIONS ──
  async function deleteListing(id) {
    if (!window.confirm('Delete this listing?')) return
    await supabase.from('listings').delete().eq('id', id)
    setListings(ls => ls.filter(l => l.id !== id))
    toast('Listing deleted.')
  }

  async function approveListing(id) {
    await supabase.from('listings').update({ status:'approved' }).eq('id', id)
    setListings(ls => ls.map(l => l.id === id ? { ...l, status:'approved' } : l))
    toast('Listing approved.')
  }

  async function toggleFeatured(id, current) {
    if (role < 200) return
    await supabase.from('listings').update({ is_featured: !current }).eq('id', id)
    setListings(ls => ls.map(l => l.id === id ? { ...l, is_featured: !current } : l))
    toast(`Featured ${!current ? 'enabled' : 'disabled'}.`)
  }

  async function toggleVerified(id, current) {
    if (role < 200) return
    await supabase.from('listings').update({ is_verified: !current }).eq('id', id)
    setListings(ls => ls.map(l => l.id === id ? { ...l, is_verified: !current } : l))
    toast(`Verified ${!current ? 'enabled' : 'disabled'}.`)
  }

  // ── REPORT ACTIONS ──
  async function dismissReport(id) {
    await supabase.from('reports').update({ status:'dismissed' }).eq('id', id)
    setReports(rs => rs.filter(r => r.id !== id))
    toast('Report dismissed.')
  }

  async function dismissRemoval(id) {
    await supabase.from('removal_requests').update({ status:'dismissed' }).eq('id', id)
    setRemovals(rs => rs.filter(r => r.id !== id))
    toast('Removal request dismissed.')
  }

  async function resolveRemoval(id, listingName) {
    // Mark resolved — admin still needs to manually find and delete the listing
    await supabase.from('removal_requests').update({ status:'resolved' }).eq('id', id)
    setRemovals(rs => rs.filter(r => r.id !== id))
    toast(`Marked resolved. Search for "${listingName}" in Listings tab to delete it.`)
  }

  async function resolveReport(id, targetId, targetType) {
    // Mark report resolved and delete the offending content
    await supabase.from('reports').update({ status:'resolved' }).eq('id', id)
    if (targetType === 'listing') {
      await supabase.from('listings').delete().eq('id', targetId)
      setListings(ls => ls.filter(l => l.id !== targetId))
    } else {
      await supabase.from('blog_posts').delete().eq('id', targetId)
      setPosts(ps => ps.filter(p => p.id !== targetId))
    }
    setReports(rs => rs.filter(r => r.id !== id))
    toast('Content removed and report resolved.')
  }
  async function deletePost(id) {
    if (!window.confirm('Delete this post?')) return
    await supabase.from('blog_posts').delete().eq('id', id)
    setPosts(ps => ps.filter(p => p.id !== id))
    toast('Post deleted.')
  }

  async function approvePost(id) {
    await supabase.from('blog_posts').update({ status:'approved' }).eq('id', id)
    setPosts(ps => ps.map(p => p.id === id ? { ...p, status:'approved' } : p))
    toast('Post approved.')
  }

  // ── USER ACTIONS (300 only) ──
  async function setUserRole(id, newRole) {
    if (role < 300) return
    await supabase.from('profiles').update({ role: newRole }).eq('id', id)
    setUsers(us => us.map(u => u.id === id ? { ...u, role: newRole } : u))
    toast(`Role updated to ${newRole}.`)
  }

  if (authLoading || loading) return (
    <div><Navbar /><p style={{ textAlign:'center', padding:'80px 20px', color:'var(--gray-400)' }}>Loading…</p></div>
  )

  const ROLE_LABEL = { 0:'Public', 100:'Moderator', 200:'Admin', 300:'Owner' }
  const ROLE_COLOR = { 0:'var(--gray-400)', 100:'var(--green)', 200:'var(--blue)', 300:'var(--gold)' }

  const TABS = [
    { id:'listings', label:'📋 Listings', min:100 },
    { id:'pending',  label:`⏳ Pending Review ${listings.filter(l=>l.status==='pending'||l.status==='flagged').length > 0 ? `(${listings.filter(l=>l.status==='pending'||l.status==='flagged').length})` : ''}`, min:100 },
    { id:'reports',  label:`🚩 Reports ${reports.length > 0 ? `(${reports.length})` : ''}`, min:100 },
    { id:'removals', label:`📬 Removal Requests ${removals.length > 0 ? `(${removals.length})` : ''}`, min:100 },
    { id:'claims',   label:`🏷 Ownership Claims ${claims.length > 0 ? `(${claims.length})` : ''}`, min:100 },
    { id:'posts',    label:'📝 Blog Posts', min:100 },
    { id:'users',    label:'👥 Users', min:300 },
  ].filter(t => role >= t.min)

  const pendingListings  = listings.filter(l => l.status !== 'approved')
  const flaggedListings  = listings.filter(l => l.status === 'flagged')
  const pendingPosts     = posts.filter(p => p.status !== 'approved')

  return (
    <div>
      <Navbar />
      <div className="page-wrap">
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28, flexWrap:'wrap', gap:12 }}>
          <div>
            <div className="page-title">Admin Dashboard</div>
            <div style={{ fontSize:13, color:'var(--gray-500)', marginTop:2 }}>
              Logged in as <strong>{user?.email}</strong> ·
              <span style={{ marginLeft:6, fontFamily:'var(--mono)', fontSize:11, background: ROLE_COLOR[role]+'22', color: ROLE_COLOR[role], padding:'2px 7px', borderRadius:4, fontWeight:700 }}>
                {ROLE_LABEL[role]} (Level {role})
              </span>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[
              { label:'Total Listings', val: stats.listings },
              { label:'Pending Review', val: listings.filter(l=>l.status==='pending'||l.status==='flagged').length, alert: listings.filter(l=>l.status==='pending'||l.status==='flagged').length > 0 },
              { label:'Open Reports', val: reports.length, alert: reports.length > 0 },
              { label:'Removal Requests', val: removals.length, alert: removals.length > 0 },
              { label:'Ownership Claims', val: claims.length, alert: claims.length > 0 },
              { label:'Blog Posts', val: stats.posts },
            ].map(s => (
              <div key={s.label} style={{ background: s.alert ? '#fef3c7' : '#fff', border:`1.5px solid ${s.alert ? 'var(--gold)' : 'var(--gray-200)'}`, borderRadius:10, padding:'10px 16px', textAlign:'center', minWidth:100 }}>
                <div style={{ fontSize:22, fontWeight:800, color: s.alert ? 'var(--gold)' : 'var(--gray-900)' }}>{s.val ?? '—'}</div>
                <div style={{ fontSize:11, color:'var(--gray-500)', fontFamily:'var(--mono)', letterSpacing:.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {msg && <div className="toast success" style={{ position:'relative', bottom:'auto', right:'auto', marginBottom:16 }}>{msg}</div>}

        {/* Role legend */}
        <div style={{ background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:10, padding:'12px 16px', marginBottom:24, display:'flex', gap:20, flexWrap:'wrap' }}>
          <span style={{ fontSize:12, fontWeight:700, color:'var(--gray-500)' }}>Access Levels:</span>
          {Object.entries(ROLE_LABEL).filter(([k]) => k > 0).map(([k,v]) => (
            <span key={k} style={{ fontSize:12, color: ROLE_COLOR[k], fontWeight:600 }}>
              {k} — {v} {parseInt(k) === role ? '(you)' : ''}
            </span>
          ))}
        </div>

        {/* Tabs */}
        <div className='admin-tabs' style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ fontSize:13, fontWeight:600, padding:'8px 18px', borderRadius:8, border:`1.5px solid ${tab===t.id?'var(--blue)':'var(--gray-200)'}`, background:tab===t.id?'var(--blue)':'#fff', color:tab===t.id?'#fff':'var(--gray-600)', cursor:'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── PENDING TAB ── */}
        {tab === 'pending' && (
          <div>
            {listings.filter(l => l.status === 'pending' || l.status === 'flagged').length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:'var(--gray-400)' }}>
                <div style={{ fontSize:36, marginBottom:10 }}>✅</div>
                <p>All caught up — no listings pending review.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Name</th><th>Category</th><th>Region</th><th>Status</th><th>Submitted</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {listings.filter(l => l.status === 'pending' || l.status === 'flagged').map(l => (
                      <tr key={l.id} style={{ background: l.status === 'flagged' ? '#fef2f2' : '#fffbeb' }}>
                        <td><strong style={{ fontSize:13 }}>{l.name}</strong><br/><span style={{ fontSize:11, color:'var(--gray-400)' }}>{l.city}, {l.country}</span></td>
                        <td><span style={{ fontFamily:'var(--mono)', fontSize:10, textTransform:'uppercase' }}>{l.category}</span></td>
                        <td style={{ fontSize:12, color:'var(--gray-500)' }}>{l.region}</td>
                        <td>
                          <span style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:4, background:l.status==='flagged'?'#fee2e2':'#fef3c7', color:l.status==='flagged'?'#dc2626':'#92400e' }}>
                            {l.status === 'flagged' ? '🚩 Flagged' : '⏳ Pending'}
                          </span>
                        </td>
                        <td style={{ fontSize:11, color:'var(--gray-400)' }}>{new Date(l.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            <button onClick={() => approveListing(l.id)} className="btn btn-primary" style={{ fontSize:11, padding:'4px 10px' }}>✓ Approve</button>
                            <button onClick={() => deleteListing(l.id)} className="btn btn-danger" style={{ fontSize:11, padding:'4px 10px' }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── REPORTS TAB ── */}
        {tab === 'reports' && (
          <div>
            {reports.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:'var(--gray-400)' }}>
                <div style={{ fontSize:36, marginBottom:10 }}>🎉</div>
                <p>No open reports — community is behaving!</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Reported Content</th><th>Type</th><th>Reason</th><th>Details</th><th>Reported</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r.id} style={{ background:'#fef2f2' }}>
                        <td><strong style={{ fontSize:13 }}>{r.target_name}</strong></td>
                        <td><span style={{ fontFamily:'var(--mono)', fontSize:10, textTransform:'uppercase' }}>{r.target_type}</span></td>
                        <td style={{ fontSize:12, color:'var(--gray-700)', maxWidth:200 }}>{r.reason}</td>
                        <td style={{ fontSize:12, color:'var(--gray-500)', maxWidth:200 }}>{r.details || '—'}</td>
                        <td style={{ fontSize:11, color:'var(--gray-400)' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <button onClick={() => resolveReport(r.id, r.target_id, r.target_type)} className="btn btn-danger" style={{ fontSize:11, padding:'4px 10px' }}>
                              Remove Content
                            </button>
                            <button onClick={() => dismissReport(r.id)} className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }}>
                              Dismiss
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── LISTINGS TAB ── */}
        {tab === 'listings' && (
          <div>
            {/* Pending/flagged first */}
            {pendingListings.length > 0 && (
              <div style={{ background:'#fffbeb', border:'1.5px solid var(--gold)', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#92400e', marginBottom:4 }}>⚠️ {pendingListings.length} listing(s) pending review</div>
                <div style={{ fontSize:12, color:'#a16207' }}>These were flagged or submitted while auto-publish was off. Review below.</div>
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th><th>Category</th><th>Region</th><th>Status</th>
                    <th>Featured</th><th>Verified</th><th>Votes</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map(l => (
                    <tr key={l.id} style={{ background: l.status === 'flagged' ? '#fef2f2' : l.status !== 'approved' ? '#fffbeb' : '' }}>
                      <td><strong style={{ fontSize:13 }}>{l.name}</strong><br/><span style={{ fontSize:11, color:'var(--gray-400)' }}>{l.city}, {l.country}</span></td>
                      <td><span style={{ fontFamily:'var(--mono)', fontSize:10, textTransform:'uppercase' }}>{l.category}</span></td>
                      <td style={{ fontSize:12, color:'var(--gray-500)' }}>{l.region}</td>
                      <td>
                        <span style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:4, background: l.status==='approved'?'#d1fae5':l.status==='flagged'?'#fee2e2':'#fef3c7', color: l.status==='approved'?'#065f46':l.status==='flagged'?'#dc2626':'#92400e' }}>
                          {l.status}
                        </span>
                      </td>
                      <td>
                        {role >= 200 ? (
                          <button onClick={() => toggleFeatured(l.id, l.is_featured)}
                            style={{ fontSize:11, padding:'3px 8px', borderRadius:5, border:`1px solid ${l.is_featured?'var(--gold)':'var(--gray-200)'}`, background:l.is_featured?'var(--gold)':'transparent', color:l.is_featured?'#fff':'var(--gray-500)', cursor:'pointer' }}>
                            {l.is_featured ? '⭐ On' : '☆ Off'}
                          </button>
                        ) : <span>{l.is_featured ? '⭐' : '—'}</span>}
                      </td>
                      <td>
                        {role >= 200 ? (
                          <button onClick={() => toggleVerified(l.id, l.is_verified)}
                            style={{ fontSize:11, padding:'3px 8px', borderRadius:5, border:`1px solid ${l.is_verified?'var(--green)':'var(--gray-200)'}`, background:l.is_verified?'var(--green)':'transparent', color:l.is_verified?'#fff':'var(--gray-500)', cursor:'pointer' }}>
                            {l.is_verified ? '✅ Yes' : '○ No'}
                          </button>
                        ) : <span>{l.is_verified ? '✅' : '—'}</span>}
                      </td>
                      <td style={{ fontFamily:'var(--mono)', fontSize:12 }}>▲{l.vote_count||0}</td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          {l.status !== 'approved' && (
                            <button onClick={() => approveListing(l.id)} className="btn btn-primary" style={{ fontSize:11, padding:'4px 10px' }}>Approve</button>
                          )}
                          <button onClick={() => deleteListing(l.id)} className="btn btn-danger" style={{ fontSize:11, padding:'4px 10px' }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── REMOVALS TAB ── */}
        {tab === 'removals' && (
          <div>
            {removals.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:'var(--gray-400)' }}>
                <div style={{ fontSize:36, marginBottom:10 }}>📬</div>
                <p>No open removal requests.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Listing Name</th><th>Requester</th><th>Role</th><th>Reason</th><th>Details</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {removals.map(r => (
                      <tr key={r.id} style={{ background:'#faf5ff' }}>
                        <td>
                          <strong style={{ fontSize:13 }}>{r.listing_name}</strong>
                          {r.listing_url && <div style={{ fontSize:11, color:'var(--gray-400)' }}>{r.listing_url}</div>}
                        </td>
                        <td style={{ fontSize:12 }}>
                          {r.your_name}<br/>
                          <span style={{ color:'var(--gray-400)', fontSize:11 }}>{r.your_email}</span>
                        </td>
                        <td style={{ fontSize:12, color:'var(--gray-500)' }}>{r.your_role}</td>
                        <td style={{ fontSize:12, color:'var(--gray-700)', maxWidth:180 }}>{r.reason}</td>
                        <td style={{ fontSize:12, color:'var(--gray-500)', maxWidth:160 }}>{r.details || '—'}</td>
                        <td style={{ fontSize:11, color:'var(--gray-400)' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <button onClick={() => resolveRemoval(r.id, r.listing_name)} className="btn btn-primary" style={{ fontSize:11, padding:'4px 10px' }}>
                              Mark Resolved
                            </button>
                            <button onClick={() => dismissRemoval(r.id)} className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }}>
                              Dismiss
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── CLAIMS TAB ── */}
        {tab === 'claims' && (
          <div>
            {claims.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:'var(--gray-400)' }}>
                <div style={{ fontSize:36, marginBottom:10 }}>🏷</div>
                <p>No pending ownership claims.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Listing</th><th>Claimant</th><th>Role</th><th>Proof</th><th>Notes</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {claims.map(cl => (
                      <tr key={cl.id} style={{ background:'#fefce8' }}>
                        <td>
                          <strong style={{ fontSize:13 }}>{cl.listing_name}</strong>
                        </td>
                        <td style={{ fontSize:12 }}>
                          {cl.claimant_name}<br/>
                          <span style={{ color:'var(--gray-400)', fontSize:11 }}>{cl.claimant_email}</span>
                        </td>
                        <td style={{ fontSize:12, color:'var(--gray-500)', textTransform:'capitalize' }}>{cl.role_at_biz}</td>
                        <td style={{ fontSize:12 }}>
                          {cl.proof_url
                            ? <a href={cl.proof_url} target="_blank" rel="noreferrer" style={{ color:'var(--blue)', fontSize:12 }}>View proof →</a>
                            : <span style={{ color:'var(--gray-400)' }}>No URL</span>
                          }
                        </td>
                        <td style={{ fontSize:12, color:'var(--gray-500)', maxWidth:160 }}>{cl.proof_notes || '—'}</td>
                        <td style={{ fontSize:11, color:'var(--gray-400)' }}>{new Date(cl.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <button
                              onClick={async () => {
                                // Approve: update claim status + set listing submitted_by to claimant
                                await supabase.from('ownership_claims').update({ status:'approved' }).eq('id', cl.id)
                                await supabase.from('listings').update({ submitted_by: cl.claimant_id }).eq('id', cl.listing_id)
                                setClaims(cs => cs.filter(c => c.id !== cl.id))
                                toast('Ownership approved — user can now edit their listing.')
                              }}
                              className="btn btn-primary" style={{ fontSize:11, padding:'4px 10px' }}>
                              ✓ Approve
                            </button>
                            <button
                              onClick={async () => {
                                await supabase.from('ownership_claims').update({ status:'rejected' }).eq('id', cl.id)
                                setClaims(cs => cs.filter(c => c.id !== cl.id))
                                toast('Claim rejected.')
                              }}
                              className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }}>
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── POSTS TAB ── */}
        {tab === 'posts' && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Title</th><th>Author</th><th>Type</th><th>Status</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {posts.map(p => (
                  <tr key={p.id} style={{ background: p.status==='flagged'?'#fef2f2':p.status!=='approved'?'#fffbeb':'' }}>
                    <td><strong style={{ fontSize:13 }}>{p.title}</strong></td>
                    <td style={{ fontSize:12 }}>{p.author_name}</td>
                    <td><span style={{ fontFamily:'var(--mono)', fontSize:10, textTransform:'uppercase' }}>{p.is_admin_post?'Official':'Community'}</span></td>
                    <td><span style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:4, background:p.status==='approved'?'#d1fae5':p.status==='flagged'?'#fee2e2':'#fef3c7', color:p.status==='approved'?'#065f46':p.status==='flagged'?'#dc2626':'#92400e' }}>{p.status}</span></td>
                    <td style={{ fontSize:12, color:'var(--gray-500)' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        {p.status !== 'approved' && (
                          <button onClick={() => approvePost(p.id)} className="btn btn-primary" style={{ fontSize:11, padding:'4px 10px' }}>Approve</button>
                        )}
                        {/* Mods can delete community posts; admins can delete all */}
                        {(role >= 100 && !p.is_admin_post) || role >= 200 ? (
                          <button onClick={() => deletePost(p.id)} className="btn btn-danger" style={{ fontSize:11, padding:'4px 10px' }}>Delete</button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── USERS TAB (300 only) ── */}
        {tab === 'users' && role >= 300 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Username</th><th>Email</th><th>Role</th><th>Joined</th><th>Change Role</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><strong style={{ fontSize:13 }}>{u.username || '—'}</strong></td>
                    <td style={{ fontSize:12, color:'var(--gray-500)' }}>{u.email}</td>
                    <td>
                      <span style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:4, background: ROLE_COLOR[u.role]+'22', color: ROLE_COLOR[u.role] }}>
                        {ROLE_LABEL[u.role] || 'Public'} ({u.role})
                      </span>
                    </td>
                    <td style={{ fontSize:12, color:'var(--gray-500)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      {u.id !== user.id ? (
                        <select value={u.role || 0} onChange={e => setUserRole(u.id, parseInt(e.target.value))}
                          style={{ fontSize:12, borderRadius:6, border:'1px solid var(--gray-200)', padding:'4px 8px' }}>
                          <option value={0}>0 — Public</option>
                          <option value={100}>100 — Moderator</option>
                          <option value={200}>200 — Admin</option>
                          <option value={300}>300 — Owner</option>
                        </select>
                      ) : <span style={{ fontSize:12, color:'var(--gray-400)' }}>You</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
